import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Grid,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { CardState } from "@/global/store/data/purchaseSeats/card";
import React from "react";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { StreetAutoCompleteV2 } from "@/shared/base/components/StreetAutoCompleteV2";
import {
  CardNumber,
  Cvv,
  FirstName,
  LastName,
  ZipCode,
} from "@/shared/base/components/FormComponents/FormComponents";
import ExpDate from "@/shared/base/components/FormComponents/ExpDate";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { IosSwitch } from "@/shared/base/components/IosSwitch/IosSwitch";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { toast } from "react-toastify";

interface FormContainerProps {
  savedCard: CardState;
  addNewCard: boolean;
  onAddNewCard: (value: boolean) => void;
  isPaymentDue: boolean;
  onSubmit: (data: any) => void;

  // render props for additional details form
  renderAdditionalBase?: () => React.ReactNode;
  renderAdditionalDisclaimer?: () => React.ReactNode;
  isSaveCardAllowed: boolean;
  disableSaveCard?: boolean; // used to disable save card switch if user selects partial payment plan
  paymentMethodTitle: string;
  zipCodePlaceholder?: string;
}

export function CheckoutForm({
  savedCard,
  onAddNewCard,
  isPaymentDue,
  addNewCard,
  onSubmit,
  // render props for additional details form
  renderAdditionalBase,
  renderAdditionalDisclaimer,
  isSaveCardAllowed,
  disableSaveCard,
  paymentMethodTitle,
  zipCodePlaceholder,
}: FormContainerProps) {
  const theme = useTheme();
  const { sm: smDown } = useCssMediaQuery("down");
  const methods = useForm();
  const { handleSubmit } = methods;

  const isCollapseOpen =
    isPaymentDue && (savedCard.ccSeqId == "" || addNewCard);

  const onError = (error: any) => {
    if (smDown) {
      toast.error(
        "Please complete all required fields before proceeding to checkout."
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        id="purchase-seat-checkout-form"
      >
        <Box>
          <Stack>
            {isPaymentDue && savedCard.ccSeqId !== "" ? (
              <>
                <Box
                  mt={3}
                  mb={2}
                  sx={{
                    backgroundColor: theme.palette.grey[100],
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <Stack
                    direction={smDown ? "column" : "row"}
                    justifyContent={"space-between"}
                    spacing={2}
                  >
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <CreditCardRoundedIcon />
                      <Typography fontWeight={"500"}>
                        Card on File:{" "}
                        <span>
                          <b>{savedCard?.dataMask}</b>
                        </span>
                      </Typography>
                    </Stack>
                    <Button
                      variant={addNewCard ? "outlined" : "contained"}
                      sx={{ borderRadius: "6px" }}
                      onClick={() => onAddNewCard(!addNewCard)}
                    >
                      {addNewCard ? "Use Current Card" : "Add New Card"}
                    </Button>{" "}
                  </Stack>
                </Box>
              </>
            ) : null}
            <Collapse in={isCollapseOpen}>
              {isCollapseOpen ? (
                <>
                  <Box mt={2}>
                    <Typography fontSize={"20px"} fontWeight={700}>
                      Billing Details
                    </Typography>
                  </Box>

                  <Box mt={1}>
                    <Grid container rowSpacing={1} columnSpacing={2}>
                      <Grid item xs={12}>
                        <StreetAutoCompleteV2 zipFieldName="zipCode" />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          placeholder="Apt/Unit"
                          size="small"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <ZipCode placeholder={zipCodePlaceholder} />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box mt={1}>
                    <Typography fontSize={"20px"} fontWeight={700}>
                      {paymentMethodTitle}
                    </Typography>
                  </Box>
                  <Box mt={1}>
                    <Grid container rowSpacing={1} columnSpacing={2}>
                      <Grid item xs={6}>
                        <FirstName />
                      </Grid>

                      <Grid item xs={6}>
                        <LastName />
                      </Grid>

                      <Grid item xs={12}>
                        <CardNumber />
                      </Grid>
                      <Grid item xs={6}>
                        <ExpDate name={"cardExpiry"} />
                      </Grid>
                      <Grid item xs={6}>
                        <Cvv />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box mt={2}>
                    <SaveCardSwitch
                      isSaveCardAllowed={isSaveCardAllowed}
                      disabled={disableSaveCard}
                    />
                  </Box>
                </>
              ) : null}
            </Collapse>

            {/*  Additional details form base */}
            {renderAdditionalBase?.()}

            <Box pt={2}>
              <CheckoutTerms />
            </Box>

            {/*  Additional details form disclaimer */}
            {renderAdditionalDisclaimer?.()}
          </Stack>
          <Button
            id="submit-purchaseSeats-checkout"
            fullWidth
            type="submit"
            variant="contained"
            sx={{
              visibility: "hidden",
              opacity: 0,
              height: 0,
              width: 0,
              mt: 2,
              textTransform: "none",
            }}
          >
            Submit
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
}

export function CheckoutTerms() {
  const theme = useTheme();
  const { languages, uiConfigs } = useTeamConfig();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const termsAndConditionsDefaultChecked = uiConfigs?.checkoutPage?.checkoutForm?.termsAndConditionsDefaultChecked; // prettier-ignore

  return (
    <>
      <Stack direction={"row"} alignItems={"flex-start"}>
        <Box>
          <Checkbox
            {...register("termsAgreed", {
              required: "You need to accept the terms and conditions",
            })}
            sx={{ marginLeft: "-8px", marginTop: "-8px" }}
            defaultChecked={termsAndConditionsDefaultChecked}
          />
        </Box>
        <Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {languages?.checkoutTermsAndCondition}
            </Typography>
          </Box>
          <Box>
            {errors?.termsAgreed?.message ? (
              <Typography
                variant="caption"
                color={theme.palette.error.main}
                lineHeight={1.1}
              >
                {errors?.termsAgreed?.message as string}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Stack>
    </>
  );
}

interface SaveCardSwitchProps {
  isSaveCardAllowed: boolean;
  disabled?: boolean;
}

export function SaveCardSwitch({
  isSaveCardAllowed,
  disabled,
}: SaveCardSwitchProps) {
  const { control } = useFormContext();

  return (
    <>
      {isSaveCardAllowed ? (
        <Stack direction={"row"} alignItems={"center"} spacing={2}>
          <Controller
            name="saveCard"
            control={control}
            defaultValue={false}
            render={({ field }) => {
              return (
                <IosSwitch
                  disabled={disabled}
                  checked={field.value || disabled}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              );
            }}
          />

          <Typography
            variant="body1"
            fontSize="14px"
            fontWeight={"600"}
            color="text.primary"
          >
            Save card for future purchase
          </Typography>
        </Stack>
      ) : null}
    </>
  );
}
